/**
 * client/net/interp.ts — snapshot buffer + remote entity interpolation [NET §7.4].
 * OWNER: NETCODE agent.
 *
 * - Buffer of the last ~1.6 s of snapshots; snapshots whose time does not advance are dropped
 *   (BO2 "CG_ProcessSnapshots: Server time went backwards").
 * - Each frame find s0.t <= renderTime < s1.t, f = (t - s0.t) / (s1.t - s0.t).
 *   Position: lerp. Angles: shortest-arc lerp. Discrete fields (stance, weapon, flags, anims): s0.
 * - spawnCount differs between s0 and s1 -> snap to s1 (respawn/teleport: never slide across the map).
 * - No s1 (snapshot late): extrapolate live players / flying missiles along velocity for at most
 *   EXTRAPOLATE_MAX_MS, then hold. The clock's -2 ms drift then grows the buffer by itself.
 * - Missiles stuck in a player (state 2) are drawn at parent.interpolatedPose * localOffset
 *   (shared/protocol/entities.ts), so a bolt in someone moves with their body; the local player's
 *   parent pose is its predicted view.
 */
import { EXTRAPOLATE_MAX_MS, MISSILE_GRAVITY } from '@shared/constants';
import { lerpAngle, vcopy, type Vec3 } from '@shared/math';
import { stuckOffsetToWorld } from '@shared/protocol/entities';
import {
  MissileState, MoveFlag,
  type MissileEntityState, type PlayerEntityState, type PlayerInfo, type Snapshot,
} from '@shared/types';
import type { RenderMissile, RenderPlayer } from '../types';

export const SNAPSHOT_BUFFER_SIZE = 32;

export class SnapshotBuffer {
  readonly snaps: Snapshot[] = [];
  /** Dropped out-of-order / duplicate snapshots. */
  dropped = 0;
  /** [dropped serverTime, newest serverTime] of the last drop (debug). */
  lastDrop: [number, number] | null = null;
  push(s: Snapshot): boolean {
    const last = this.snaps[this.snaps.length - 1];
    if (last && s.serverTime <= last.serverTime) { this.dropped++; this.lastDrop = [s.serverTime, last.serverTime]; return false; }
    this.snaps.push(s);
    while (this.snaps.length > SNAPSHOT_BUFFER_SIZE) this.snaps.shift();
    return true;
  }
  clear(): void { this.snaps.length = 0; }
  get latest(): Snapshot | undefined { return this.snaps[this.snaps.length - 1]; }
  /** Bracketing snapshots for time t: s0.t <= t < s1.t (s1 null = extrapolate from s0). */
  bracket(t: number): { s0: Snapshot; s1: Snapshot | null } | null {
    const n = this.snaps.length;
    if (!n) return null;
    for (let i = n - 1; i >= 0; i--) {
      if (this.snaps[i].serverTime <= t) return { s0: this.snaps[i], s1: this.snaps[i + 1] ?? null };
    }
    return { s0: this.snaps[0], s1: null }; // t before the oldest snapshot: hold the oldest
  }
}

const unknownInfo = (clientNum: number): PlayerInfo => ({ clientNum, name: `player${clientNum}`, isBot: false, body: 0, ping: 0, ready: true });

function findPlayer(list: readonly PlayerEntityState[], clientNum: number): PlayerEntityState | undefined {
  for (const p of list) if (p.clientNum === clientNum) return p;
  return undefined;
}

/** Interpolate remote players at render time t into `out` (fresh objects: consumers may keep them). */
export function interpolatePlayers(buf: SnapshotBuffer, t: number, info: ReadonlyMap<number, PlayerInfo>, out: RenderPlayer[]): void {
  const br = buf.bracket(t);
  if (!br) { out.length = 0; return; }
  const { s0, s1 } = br;
  const f = s1 ? Math.min(1, Math.max(0, (t - s0.serverTime) / (s1.serverTime - s0.serverTime))) : 0;
  const extS = s1 ? 0 : Math.max(0, Math.min(EXTRAPOLATE_MAX_MS, t - s0.serverTime)) * 0.001;
  let k = 0;
  for (const a of s0.players) {
    const b = s1 ? findPlayer(s1.players, a.clientNum) : undefined;
    const r = blankPlayer();
    out[k++] = r;
    const pi = info.get(a.clientNum) ?? unknownInfo(a.clientNum);
    if (b && b.spawnCount !== a.spawnCount) fillPlayer(r, b, null, 0, 0, pi); // respawn: snap to s1
    else fillPlayer(r, a, b ?? null, f, b ? 0 : extS, pi);
  }
  out.length = k;
}

function blankPlayer(): RenderPlayer {
  return {
    clientNum: 0, info: unknownInfo(0), origin: [0, 0, 0], angles: [0, 0, 0], velocity: [0, 0, 0], stance: 0, viewHeight: 0,
    moveFlags: 0, eFlags: 0, weapon: 0, weaponState: 0, legsAnim: 0, torsoAnim: 0, spawnCount: 0, alive: false,
  };
}

function fillPlayer(r: RenderPlayer, a: PlayerEntityState, b: PlayerEntityState | null, f: number, extS: number, info: PlayerInfo): void {
  const alive = !(a.moveFlags & MoveFlag.DEAD);
  for (let i = 0; i < 3; i++) {
    if (b) {
      r.origin[i] = a.origin[i] + (b.origin[i] - a.origin[i]) * f;
      r.angles[i] = lerpAngle(a.angles[i], b.angles[i], f);
      r.velocity[i] = a.velocity[i] + (b.velocity[i] - a.velocity[i]) * f;
    } else {
      // extrapolate live players only; never into the floor for the vertical part (ESTIMATE: hold z)
      const v = alive && i < 2 ? a.velocity[i] : 0;
      r.origin[i] = a.origin[i] + v * extS;
      r.angles[i] = a.angles[i];
      r.velocity[i] = a.velocity[i];
    }
  }
  r.clientNum = a.clientNum;
  r.info = info;
  r.stance = a.stance;
  r.viewHeight = b ? a.viewHeight + (b.viewHeight - a.viewHeight) * f : a.viewHeight;
  r.moveFlags = a.moveFlags;
  r.eFlags = a.eFlags;
  r.weapon = a.weapon;
  r.weaponState = a.weaponState;
  r.legsAnim = a.legsAnim;
  r.torsoAnim = a.torsoAnim;
  r.spawnCount = a.spawnCount;
  r.alive = alive;
}

/** Pose source for stuck-to-player missiles: remote interpolated players + the local player. */
export interface ParentPose {
  (clientNum: number, outOrigin: Vec3): number | null; // returns yaw (deg) or null if unknown
}

/** World transform of a missile from its (possibly parent-relative) state. */
export function resolveMissile(m: MissileEntityState, parentPose: ParentPose, outPos: Vec3, outAngles: Vec3): boolean {
  if (m.state === MissileState.STUCK_PLAYER) {
    const po: Vec3 = [0, 0, 0];
    const yaw = parentPose(m.parent, po);
    if (yaw === null) return false;
    stuckOffsetToWorld(outPos, m.origin, po, yaw);
    outAngles[0] = m.angles[0]; outAngles[1] = m.angles[1] + yaw; outAngles[2] = m.angles[2];
    return true;
  }
  vcopy(outPos, m.origin);
  vcopy(outAngles, m.angles);
  return true;
}

function findMissile(list: readonly MissileEntityState[], entId: number): MissileEntityState | undefined {
  for (const m of list) if (m.entId === entId) return m;
  return undefined;
}

export function interpolateMissiles(buf: SnapshotBuffer, t: number, parentPose: ParentPose, out: RenderMissile[]): void {
  out.length = 0;
  const br = buf.bracket(t);
  if (!br) return;
  const { s0, s1 } = br;
  const f = s1 ? Math.min(1, Math.max(0, (t - s0.serverTime) / (s1.serverTime - s0.serverTime))) : 0;
  const extS = s1 ? 0 : Math.max(0, Math.min(EXTRAPOLATE_MAX_MS, t - s0.serverTime)) * 0.001;
  const pa: Vec3 = [0, 0, 0], aa: Vec3 = [0, 0, 0], pb: Vec3 = [0, 0, 0], ab: Vec3 = [0, 0, 0];
  for (const a of s0.missiles) {
    const b = s1 ? findMissile(s1.missiles, a.entId) : undefined;
    if (!resolveMissile(a, parentPose, pa, aa)) continue;
    const origin: Vec3 = [pa[0], pa[1], pa[2]];
    const angles: Vec3 = [aa[0], aa[1], aa[2]];
    const sameFrame = b && b.state === a.state && (a.state !== MissileState.STUCK_PLAYER || a.parent === b.parent);
    const worldToWorld = b && a.state !== MissileState.STUCK_PLAYER && b.state !== MissileState.STUCK_PLAYER;
    if (b && (sameFrame || worldToWorld) && resolveMissile(b, parentPose, pb, ab)) {
      for (let i = 0; i < 3; i++) { origin[i] = pa[i] + (pb[i] - pa[i]) * f; angles[i] = lerpAngle(aa[i], ab[i], f); }
    } else if (!s1 && a.state === MissileState.FLYING) {
      origin[0] += a.velocity[0] * extS;
      origin[1] += a.velocity[1] * extS;
      origin[2] += a.velocity[2] * extS - 0.5 * MISSILE_GRAVITY * extS * extS; // ESTIMATE: TR_GRAVITY extrapolation
    }
    out.push({
      entId: a.entId, kind: a.kind, owner: a.owner, state: a.state, origin, angles,
      velocity: [a.velocity[0], a.velocity[1], a.velocity[2]], explodeAt: a.explodeAt, predicted: false,
    });
  }
}
